import { useState } from "react";
import { useStore } from "react-redux";

// reducers
import { success, error as toastError } from "hyc-store/reducers/toast";

// components
import { SelectWeek } from "shared/components";
import { Button } from "shared/components/bulma/button";
import { Select } from "shared/components/bulma/select";
import { ActionBar } from "shared/components/action-bar";
import { Checkbox } from "shared/components/bulma/checkbox";

// hooks
import { useActiveUser } from "shared/hooks/use-active-user";

// constants
import * as constants from '../constants';

import * as helper from "./helper";
import { historize } from "./historize";

const InputControl = ({ title, value, name, onChange, tabIndex = 0, className = '' }) => {
    if (title && name) {
        return (
            <div className="field mr-6">
                <label className="label is-size-6">{title}</label>
                <div className="control">
                    <input className={`input ${className}`} type="text"
                        value={value} name={name} onChange={onChange} tabIndex={tabIndex} />
                </div>
            </div>
        );
    } else {
        return null;
    }
}

const Billing = ({ customer, onUpdate }) => {

    const store = useStore();
    const user = useActiveUser();

    const [dirty, setDirty] = useState(false);
    const [info, setInfo] = useState(customer);
    const [loading, setLoading] = useState(false);

    const handleChange = (event) => {
        const { name, value } = event.target;
        const data = { ...info, [name]: value };
        setInfo(data);
        setDirty(true);
    }
    const handleChangeCheckbox = () => {
        const { win_back } = info;
        const winBack = win_back?.active ?? false;
        const timestamp = win_back?.from ?? +new Date();
        const data = { ...info, win_back: { active: !winBack, from: timestamp } };
        setInfo(data);
        setDirty(true);
    }

    const save = async () => {
        setLoading(true);

        try {
            await helper.save({ ...info, full_name: `${info.first_name} ${info.last_name}` });
            const history = historize({ customer, changes: info });
            await helper.saveHistory(customer, history, user);
            store.dispatch(success('Succesfully saved delivery information'));
            setDirty(false);
            onUpdate(+new Date());
        } catch (error) {
            store.dispatch(
                toastError([
                    'Something went wrong while saving the customer delivery information, ',
                    'please contact the tech team to report.'
                ].join(' '))
            );
        }

        setLoading(false);
    }

    return (
        <>
            <div className="columns">
                <div className="column">

                    <div className="columns">
                        <div className="column">
                            <InputControl
                                title="Full Name"
                                className="is-static"
                                name="full_name"
                                tabIndex="1"
                                value={`${info.first_name} ${info.last_name}`}
                                disabled />
                        </div>
                        <div className="column">
                            <InputControl
                                title="First Name"
                                value={info.first_name}
                                name="first_name"
                                tabIndex="8"
                                onChange={handleChange}
                            />
                        </div>
                        <div className="column">
                            <InputControl
                                title="Last Name"
                                value={info.last_name}
                                name="last_name"
                                tabIndex="9"
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="columns is-vcentered">
                        <div className="column is-8">


                            <InputControl
                                title="Street address and N*"
                                tabIndex="2"
                                value={info.address}
                                name="address"
                                onChange={handleChange}
                            />

                            <InputControl
                                title="Postcode"
                                tabIndex="3"
                                value={info.zip_code}
                                name="zip_code"
                                onChange={handleChange}
                            />

                            <InputControl
                                title="Email"
                                tabIndex="4"
                                value={info.email}
                                name="email"
                                onChange={handleChange}
                            />

                            <InputControl
                                title="Phone"
                                tabIndex="5"
                                value={info.phone}
                                name="phone"
                                onChange={handleChange}
                            />

                            <InputControl
                                title="City"
                                tabIndex="6"
                                value={info.city}
                                name="city"
                                onChange={handleChange}
                            />

                            <InputControl
                                title="Door code"
                                tabIndex="7"
                                value={info.building_code}
                                name="building_code"
                                onChange={handleChange}
                            />

                            <label className="label is-size-6">Country</label>
                            <Select
                                value={info.country}
                                className="is-disabled"
                                name="country"
                                options={['France', 'Sweden']}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="column">
                            <label className="label is-size-6">Distribution partner</label>
                            <Select
                                name="distribution_partner"
                                value={info.distribution_partner}
                                options={constants.DISTRIBUTION_PARTNERS}
                                onChange={handleChange} />

                            <label className="label is-size-6 mt-3">Delivery Week</label>
                            <SelectWeek
                                name="delivery_week"
                                value={info.delivery_week} onChange={handleChange} />

                            <label className="label is-size-6 mt-3">Win Back</label>
                            <Checkbox name="win_back"
                                onChange={handleChangeCheckbox}
                                checked={info.win_back?.active}
                            >
                                Active
                            </Checkbox>

                        </div>
                    </div>

                </div>
                <div className="column is-3"></div>
            </div>

            {dirty && (
                <ActionBar>
                    <Button className={`is-primary ${loading ? 'is-loading' : ''}`}
                        onClick={save} disabled={loading}>Save Changes</Button>
                </ActionBar>
            )}
        </>
    );
}

export { Billing };
